import { Locales, getField } from "@simplyk/common";

import { RateOutput } from "@/common/types/trpc";

export interface VariantValueDisplay {
  id: string;
  name: string;
  isSoldOut: boolean;
}

export interface VariantGroupDisplay {
  id: string;
  name: string;
  values: VariantValueDisplay[];
}

interface RateLike {
  id: string;
  minimumToBuy?: number | null;
  maximumToBuy?: number | null;
  seats?: number | null;
}

interface ChildRateLike {
  childRateId: string;
  seats?: number | null;
}

interface CartLine {
  rateId: string;
  purchasedNumber?: number | null;
}

export interface RateAvailability {
  minQuantity: number;
  maxQuantity: number | null;
  remainingStock: number | null;
  alreadyInCart: number;
  isUnavailable: boolean;
}

export function computeRateAvailability(params: {
  rate: RateLike;
  childRate?: ChildRateLike | null;
  soldCountByRateId: Map<string, number>;
  ticketsPurchased?: CartLine[] | undefined;
}): RateAvailability {
  const { rate, childRate, soldCountByRateId, ticketsPurchased } = params;

  const targetRateId = childRate?.childRateId ?? rate.id;
  const seats = childRate ? childRate.seats : rate.seats;

  const minQuantity = Math.max(rate.minimumToBuy ?? 1, 1);

  const remainingStock =
    seats === null || seats === undefined ? null : Math.max(0, seats - (soldCountByRateId.get(targetRateId) ?? 0));

  const maxPerPurchase = rate.maximumToBuy && rate.maximumToBuy > 0 ? rate.maximumToBuy : null;

  const alreadyInCart = ticketsPurchased?.find((line) => line.rateId === targetRateId)?.purchasedNumber ?? 0;

  let maxQuantity: number | null;
  if (remainingStock === null && maxPerPurchase === null) {
    maxQuantity = null;
  } else {
    const effectiveCap = Math.min(remainingStock ?? Infinity, maxPerPurchase ?? Infinity);
    maxQuantity = Math.max(0, effectiveCap - alreadyInCart);
  }

  const isUnavailable = maxQuantity !== null && maxQuantity < minQuantity;

  return { minQuantity, maxQuantity, remainingStock, alreadyInCart, isUnavailable };
}

export interface ShopActionStates {
  isIncrementDisabled: boolean;
  isDecrementDisabled: boolean;
  isAddToCartDisabled: boolean;
  isBelowMin: boolean;
  showMinimumCaption: boolean;
}

export function computeShopActionStates(
  quantity: number,
  availability: { minQuantity: number; maxQuantity: number | null }
): ShopActionStates {
  const { minQuantity, maxQuantity } = availability;
  const hasMaxCap = maxQuantity !== null && maxQuantity !== undefined;
  const isAtMax = hasMaxCap && quantity >= maxQuantity;
  const cannotReachMin = hasMaxCap && minQuantity > maxQuantity;
  const exceedsMax = hasMaxCap && quantity > maxQuantity;
  const isBelowMin = quantity > 0 && quantity < minQuantity;
  const showMinimumCaption = minQuantity > 1 && (!hasMaxCap || maxQuantity > 0);

  return {
    isIncrementDisabled: isAtMax || cannotReachMin,
    isDecrementDisabled: quantity <= 0,
    isAddToCartDisabled: quantity === 0 || maxQuantity === 0 || isBelowMin || cannotReachMin || exceedsMax,
    isBelowMin,
    showMinimumCaption,
  };
}

export function buildVariantGroups(
  rate: RateOutput,
  locale: Locales,
  soldCountByRateId: Map<string, number>,
  selectedVariantValues: Record<string, string> = {},
  ticketsPurchased?: CartLine[] | undefined
): VariantGroupDisplay[] {
  const shopVariants = rate.shopVariants ?? [];
  const childRatesInfo = rate.childRatesInfo ?? [];

  if (shopVariants.length === 0) {
    return [];
  }

  return shopVariants
    .filter((variant) => !variant.isArchived)
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map((variant) => {
      const variantField = getField(variant.shopVariantFields, locale);
      const variantName = variantField?.name || variant.shopVariantFields?.[0]?.name || "";

      const values = (variant.shopVariantValues ?? [])
        .filter((value) => !value.isArchived)
        .sort((a, b) => a.sortIndex - b.sortIndex)
        .map((value) => {
          const valueField = getField(value.shopVariantValueFields, locale);
          const valueName = valueField?.name || value.shopVariantValueFields?.[0]?.name || "";

          return {
            id: value.id,
            name: valueName,
            isSoldOut: isVariantValueDisabled({
              variantId: variant.id,
              valueId: value.id,
              selectedVariantValues,
              parentRate: rate,
              childRatesInfo,
              soldCountByRateId,
              ticketsPurchased,
            }),
          };
        });

      return {
        id: variant.id,
        name: variantName,
        values,
      };
    })
    .filter((group) => group.values.length > 0);
}

function isVariantValueDisabled(params: {
  variantId: string;
  valueId: string;
  selectedVariantValues: Record<string, string>;
  parentRate: RateLike;
  childRatesInfo: ChildRateInfoInput[];
  soldCountByRateId: Map<string, number>;
  ticketsPurchased?: CartLine[] | undefined;
}): boolean {
  const { variantId, valueId, selectedVariantValues, parentRate, childRatesInfo, soldCountByRateId, ticketsPurchased } =
    params;

  if (childRatesInfo.length === 0) {
    return false;
  }

  const requiredValueIdsFromOtherVariants = Object.entries(selectedVariantValues)
    .filter(([selectedVariantId, selectedValueId]) => selectedVariantId !== variantId && Boolean(selectedValueId))
    .map(([, selectedValueId]) => selectedValueId);

  const candidates = childRatesInfo.filter((child) => {
    const childValueIds = new Set(child.variantValueIds);
    if (!childValueIds.has(valueId)) {
      return false;
    }
    return requiredValueIdsFromOtherVariants.every((id) => childValueIds.has(id));
  });

  if (candidates.length === 0) {
    return true;
  }
  return candidates.every((child) =>
    isChildUnavailable({ parentRate, childRate: child, soldCountByRateId, ticketsPurchased })
  );
}

export function findMatchingChildRateId(
  selectedValues: Record<string, string>,
  childRatesInfo: Array<{ childRateId: string; variantValueIds: string[] }>
): string | null {
  const selectedValueIds = Object.values(selectedValues);
  if (selectedValueIds.length === 0) {
    return null;
  }
  const match = childRatesInfo.find((child) => {
    if (child.variantValueIds.length !== selectedValueIds.length) {
      return false;
    }
    const childValueSet = new Set(child.variantValueIds);
    return selectedValueIds.every((id) => childValueSet.has(id));
  });
  return match?.childRateId ?? null;
}

// e.g. T-shirt S Red: seats=10, soldCount=3, 2 in cart, maxToBuy=8 → maxQuantity=min(10-3, 8)-2 = 5.
// Thin wrapper around `computeRateAvailability` so legacy call-sites that only need
// the ceiling keep working.
export function computeShopMaxQuantity(params: {
  hasVariants: boolean;
  selectedVariantValues: Record<string, string>;
  childRatesInfo: Array<{ childRateId: string; variantValueIds: string[]; seats?: number | null }>;
  parentRateId: string;
  parentRateSeats: number | null | undefined;
  parentRateMaximumToBuy: number | null | undefined;
  soldCountByRateId: Map<string, number>;
  ticketsPurchased: Array<{ rateId: string; purchasedNumber?: number | null }> | undefined;
}): number | null {
  const {
    hasVariants,
    selectedVariantValues,
    childRatesInfo,
    parentRateId,
    parentRateSeats,
    parentRateMaximumToBuy,
    soldCountByRateId,
    ticketsPurchased,
  } = params;

  const matchedChild = hasVariants ? findMatchingChildRate(selectedVariantValues, childRatesInfo) : null;
  if (hasVariants && !matchedChild) {
    return null;
  }

  return computeRateAvailability({
    rate: {
      id: parentRateId,
      seats: parentRateSeats,
      maximumToBuy: parentRateMaximumToBuy,
    },
    childRate: matchedChild ? { childRateId: matchedChild.childRateId, seats: matchedChild.seats } : null,
    soldCountByRateId,
    ticketsPurchased,
  }).maxQuantity;
}

function findMatchingChildRate<T extends { childRateId: string; variantValueIds: string[] }>(
  selectedValues: Record<string, string>,
  childRatesInfo: T[]
): T | null {
  const selectedValueIds = Object.values(selectedValues);
  if (selectedValueIds.length === 0) {
    return null;
  }
  return (
    childRatesInfo.find((child) => {
      if (child.variantValueIds.length !== selectedValueIds.length) {
        return false;
      }
      const childValueSet = new Set(child.variantValueIds);
      return selectedValueIds.every((id) => childValueSet.has(id));
    }) ?? null
  );
}

interface ChildRateInfoInput {
  childRateId: string;
  variantValueIds: string[];
  seats?: number | null;
}

function isChildUnavailable(params: {
  parentRate: RateLike;
  childRate: ChildRateInfoInput;
  soldCountByRateId: Map<string, number>;
  ticketsPurchased?: CartLine[] | undefined;
}): boolean {
  return computeRateAvailability({
    rate: params.parentRate,
    childRate: { childRateId: params.childRate.childRateId, seats: params.childRate.seats },
    soldCountByRateId: params.soldCountByRateId,
    ticketsPurchased: params.ticketsPurchased,
  }).isUnavailable;
}

export function areAllChildRatesUnavailable(params: {
  parentRate: RateLike;
  childRatesInfo: ChildRateInfoInput[];
  soldCountByRateId: Map<string, number>;
  ticketsPurchased?: CartLine[] | undefined;
}): boolean {
  const { parentRate, childRatesInfo, soldCountByRateId, ticketsPurchased } = params;
  if (childRatesInfo.length === 0) {
    return false;
  }
  return childRatesInfo.every((childRate) =>
    isChildUnavailable({ parentRate, childRate, soldCountByRateId, ticketsPurchased })
  );
}

// e.g. parent T-shirt with children S-Red (qty 2) + M-Blue (qty 1) → returns 3.
// For non-variant rates, this collapses to the parent's own purchasedNumber.
export function getShopParentCartQuantity(
  parentRate: { id: string; childRatesInfo?: Array<{ childRateId: string }> | null | undefined },
  ticketsPurchased: Array<{ rateId: string; purchasedNumber?: number | null }> | undefined
): number {
  if (!ticketsPurchased || ticketsPurchased.length === 0) {
    return 0;
  }
  const childRateIds = new Set((parentRate.childRatesInfo ?? []).map((c) => c.childRateId));
  return ticketsPurchased.reduce((sum, t) => {
    const isOwnedRow = t.rateId === parentRate.id || childRateIds.has(t.rateId);
    if (!isOwnedRow || !t.purchasedNumber) {
      return sum;
    }
    return sum + t.purchasedNumber;
  }, 0);
}

export function propagateShopPwycUnitToTickets<
  T extends { rateId: string; purchasedNumber?: number | null; pwycAmount?: number | null },
>(params: {
  parentRate: { id: string; childRatesInfo?: Array<{ childRateId: string }> | null | undefined };
  ticketsPurchased: T[] | undefined;
  unitAmount: number | null;
}): Array<{ index: number; pwycAmount: number | null }> {
  const { parentRate, ticketsPurchased, unitAmount } = params;
  if (!ticketsPurchased) {
    return [];
  }
  const childRateIds = new Set((parentRate.childRatesInfo ?? []).map((c) => c.childRateId));
  const updates: Array<{ index: number; pwycAmount: number | null }> = [];
  ticketsPurchased.forEach((ticket, index) => {
    const isOwnedRow = ticket.rateId === parentRate.id || childRateIds.has(ticket.rateId);
    if (!isOwnedRow) {
      return;
    }
    const quantity = ticket.purchasedNumber ?? 0;
    if (quantity <= 0) {
      return;
    }
    const lineTotal = unitAmount === null ? null : unitAmount * quantity;
    if (ticket.pwycAmount !== lineTotal) {
      updates.push({ index, pwycAmount: lineTotal });
    }
  });
  return updates;
}
